import type { OneAccountView } from '@/api/map/oneAccount'
import type { PlStatementRowView } from '@/api/map/plStatement'
import type {
  SurveillanceCurrency,
  SurveillanceGraphFilter,
} from '@/features/surveillance/types'

/** Lab sportsbook-detail View Graph hides these betCategory values. */
const HIDDEN_BET_CATEGORIES = new Set([
  'Line Session',
  'Bookmaker',
  'Custom Session',
])

/**
 * Lab `sportsbook-detail` View Graph gate:
 * SportsBook + not Line Session / Bookmaker / Custom Session.
 * Fancy / casino / card never show the button.
 */
export function canShowPlViewGraph(detail: Pick<
  OneAccountView,
  'gameName' | 'betCategory' | 'isCardGame' | 'isLiveCasino' | 'isFundist'
>): boolean {
  if (detail.isCardGame || detail.isLiveCasino || detail.isFundist) return false
  if (detail.gameName !== 'SportsBook') return false
  return !HIDDEN_BET_CATEGORIES.has(detail.betCategory.trim())
}

/**
 * Settlement often stamps `event_id = event_name` when the vendor id is blank.
 * Agent BQ `bqGetBetsForSurv` prefers orders.vendor_event_id — UI still passes
 * the settlement stamp; this helper is for tests / future catalog resolve.
 */
export function looksLikeEventNameNotId(
  eventId: string,
  eventName?: string,
): boolean {
  const id = eventId.trim()
  if (!id) return true
  if (eventName && id.toLowerCase() === eventName.trim().toLowerCase()) return true
  if (/\s/.test(id)) return true
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)) {
    return true
  }
  return false
}

/** Archive surveillance filter from Game Detail (Lab getGraph on accountStatement[0]). */
export function plArchiveGraphFilter(
  detail: OneAccountView,
  row: PlStatementRowView,
): SurveillanceGraphFilter | null {
  const eventType = (detail.eventType || detail.subGame || row.sport).trim()
  const eventId = (detail.eventId || row.eventId).trim()
  const marketId = (detail.marketId || row.marketId).trim()
  if (!eventType || !eventId || !marketId) return null

  const currencyRaw = (detail.currencyType ?? row.currencyType ?? 'All').trim()
  const currencyType = (
    currencyRaw === 'INR' ||
    currencyRaw === 'POINTS' ||
    currencyRaw === 'POINT@1' ||
    currencyRaw === 'All'
      ? currencyRaw
      : 'All'
  ) as SurveillanceCurrency

  return {
    mode: 'archive',
    eventType,
    eventId,
    marketId,
    playerNames: detail.username.trim() ? [detail.username.trim()] : [],
    fromStake: null,
    toStake: null,
    currencyType,
  }
}

/**
 * Agent-API BQ resolves vendor_event_id from orders when the client sends a
 * display name — no catalog HTTP from Backend-UI (plane isolation).
 */
export async function resolvePlGraphEventId(opts: {
  eventId: string
  eventName: string
  marketId: string
}): Promise<string> {
  void opts.eventName
  void opts.marketId
  return opts.eventId.trim()
}
